if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';

        // ---------- mode flag (backward compatible) ----------
        const rawStickAttr = (this.dataset.stickToFeatured || 'off').toLowerCase();
        this.stickMode =
          rawStickAttr === 'true'  ? 'always' :
          rawStickAttr === 'false' ? 'off'    :
          rawStickAttr; // 'initial' | 'always' | 'off'
        this._blockedInitial = false; // used only when stickMode === 'initial'
        // ------------------------------------------------------

        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) this.initImageZoom();

        // 🔗 Wire up all variant change sources (pubsub, custom events, native select)
        this.subscribeToVariantChanges();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: { 461: { slidesPerView: 5 } },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: { direction: 'vertical', slidesPerView: 'auto' },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, { slidesPerView: 'auto' });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) this.initSlider();
          else this.destroySlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${media.querySelector('product-model').outerHTML}</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${media.querySelector('video-element').outerHTML}</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) return el;
            }
          }
          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) return el.src;
            }
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') e.preventDefault();
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => this.lightbox.pswp.next(),
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => this.lightbox.pswp.prev(),
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: () => this.lightbox.pswp.close(),
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;
          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) thumbs.swiper.slideTo(activeIndex);

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      // 🔗 Listen to ANY variant-change source and route to setActiveMedia
      subscribeToVariantChanges() {
        // 1) FoxTheme PubSub (official)
        if (window.FoxTheme?.pubsub?.subscribe && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
          FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            (event) => {
              const mySection = this.closest('product-info')?.dataset.section;
              if (!mySection) return;
              if (event?.data?.sectionId !== mySection) return;
              // Prefer full variant object if provided
              const variant = event.data.variant || null;
              if (variant) {
                this.setActiveMedia(variant);
              } else if (event.data.variantId) {
                this.setActiveMediaByVariantId(event.data.variantId);
              }
            }
          );
        }

        // 2) Generic bubbling custom event
        this.addEventListener('variant:change', (e) => {
          const variant = e.detail?.variant;
          if (variant) this.setActiveMedia(variant);
          else if (e.detail?.variantId) this.setActiveMediaByVariantId(e.detail.variantId);
        });

        // 3) ✅ Fallback: observe the main product form select[name="id"]
        const productInfo = this.closest('product-info');
        const select = productInfo?.querySelector('form [name="id"]');
        if (select) {
          select.addEventListener('change', () => {
            const variantId = Number(select.value);
            if (variantId) this.setActiveMediaByVariantId(variantId);
          });
        }
      }

      // Resolve a variant object by ID, then call setActiveMedia
      setActiveMediaByVariantId(variantId) {
        const variant = this.findVariantById(variantId);
        if (!variant) {
          console.warn('[media-gallery] Could not resolve variant for id:', variantId);
          // last-chance: try a direct media lookup if slides carry data-variant-ids
          const slide = this.findSlideByVariantId(variantId);
          if (slide && this.sliderInstance?.slider) {
            const idx = this.getSlideIndexByEl(slide);
            this.performSlideTo(idx);
          }
          return;
        }
        this.setActiveMedia(variant);
      }

      // Try to parse variant JSON from nearby script tags (product or sticky bar)
      findVariantById(variantId) {
        // Look within our section first
        const root = this.closest('[data-section]') || document;
        const scripts = root.querySelectorAll('script[type="application/json"]');
        for (const s of scripts) {
          try {
            const data = JSON.parse(s.textContent.trim());
            const arr = Array.isArray(data) ? data : (data?.variants || data?.product?.variants);
            if (Array.isArray(arr)) {
              const v = arr.find((it) => Number(it.id) === Number(variantId));
              if (v) return v;
            }
          } catch (e) { /* ignore */ }
        }
        // Check whole document as a fallback (e.g., sticky ATC JSON lives elsewhere)
        if (root !== document) {
          const more = document.querySelectorAll('script[type="application/json"]');
          for (const s of more) {
            try {
              const data = JSON.parse(s.textContent.trim());
              const arr = Array.isArray(data) ? data : (data?.variants || data?.product?.variants);
              if (Array.isArray(arr)) {
                const v = arr.find((it) => Number(it.id) === Number(variantId));
                if (v) return v;
              }
            } catch (e) { /* ignore */ }
          }
        }
        return null;
      }

      // If markup provides per-slide variant mapping (optional)
      findSlideByVariantId(variantId) {
        // Works if your slides include something like data-variant-ids="123,456"
        const all = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
        for (const el of all) {
          const ids = (el.dataset.variantIds || '').split(',').map((x) => x.trim());
          if (ids.includes(String(variantId))) return el;
        }
        return null;
      }

      // Map media ID -> real slide index (current DOM order, no duplicates)
      getSlideIndexByMediaId(mediaId) {
        if (!this.sliderInstance || !this.sliderInstance.slider) return 0;
        const { slides } = this.sliderInstance.slider;
        let real = 0;
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          if (slide.classList.contains('swiper-slide-duplicate')) continue;
          if (String(slide.dataset.mediaId) === String(mediaId)) return real;
          real++;
        }
        return 0; // fallback
      }

      getSlideIndexByEl(el) {
        if (!this.sliderInstance || !this.sliderInstance.slider) return 0;
        const { slides } = this.sliderInstance.slider;
        let real = 0;
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          if (slide.classList.contains('swiper-slide-duplicate')) continue;
          if (slide === el) return real;
          real++;
        }
        return 0;
      }

      performSlideTo(index) {
        if (this.sliderInstance?.slider) this.sliderInstance.slider.slideToLoop(index);
      }

      // Variant-driven slide activation (respects mode)
      setActiveMedia(variant) {
        if (!variant || !variant.featured_media) return;

        // Block only the first automatic attempt (initial page load)
        if (this.stickMode === 'initial' && this._blockedInitial === false) {
          this._blockedInitial = true;
          return;
        }

        // Never auto-switch in "always" mode
        if (this.stickMode === 'always') return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          const targetIndex = this.getSlideIndexByMediaId(variant.featured_media.id);
          this.performSlideTo(targetIndex);
        } else {
          this.sortMediaItems(variant);
        }
      }

      // Reorder DOM when no slider (desktop non-carousel layouts)
      sortMediaItems(variant) {
        if (!variant || !variant.featured_media) return;

        if (this.stickMode === 'initial' && this._blockedInitial === false) {
          this._blockedInitial = true;
          return;
        }
        if (this.stickMode === 'always') return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort((a, b) => a.dataset.mediaIndex - b.dataset.mediaIndex);

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => this.elements.mediaList.appendChild(media));

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
        }
      }
    }
  );
}
